import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';

import { useSession } from '@/auth/hooks';
import type { LoginLocationState } from '@/auth/RequireAuth';
import { useAdsQuery, type AdPublic } from './hooks';
import styles from './AdsFeed.module.css';

// Блок «РЕКЛАМА» на главной (V1.4) — по макету партнёра
// (docs/design/20260911-ads-feed-mockup.png): капитель, баннеры во всю ширину
// один под другим, ниже — пунктирная кнопка «Разместить рекламу».
//   * карточка целиком — внешняя ссылка на `go_url` (счётчик кликов на бэке,
//     302 на link_url рекламодателя), открывается в новой вкладке;
//   * пусто или ошибка загрузки → карточек нет, капитель и кнопка остаются:
//     блок — точка входа для рекламодателя и не должен исчезать вместе с
//     баннерами;
//   * гость по кнопке идёт на вход с возвратом на форму — тем же
//     location.state, что и RequireAuth.

export const NEW_AD_PATH = '/ads/new';

function AdCard({ ad }: { ad: AdPublic }): JSX.Element {
  return (
    <a
      className={styles.card}
      href={ad.go_url}
      target="_blank"
      rel="noreferrer noopener"
      style={{ backgroundImage: `url(${ad.image_url})` }}
      aria-label={ad.title}
    >
      <span className={styles.cardTitle}>{ad.title}</span>
    </a>
  );
}

export function AdsFeed(): JSX.Element {
  const { t } = useTranslation();
  const { isAuthenticated } = useSession();
  const ads = useAdsQuery();
  const items = ads.data?.items ?? [];

  return (
    <section className={styles.section} aria-label={t('ads.feed.title')}>
      <h2 className={styles.caption}>{t('ads.feed.title')}</h2>
      {items.length > 0 && (
        <div className={styles.list}>
          {items.map((ad) => (
            <AdCard key={ad.id} ad={ad} />
          ))}
        </div>
      )}
      {isAuthenticated ? (
        <Link to={NEW_AD_PATH} className={styles.placeButton}>
          <Megaphone size={14} aria-hidden="true" />
          {t('ads.feed.place')}
        </Link>
      ) : (
        <Link
          to="/login"
          state={{ from: NEW_AD_PATH } satisfies LoginLocationState}
          className={styles.placeButton}
        >
          <Megaphone size={14} aria-hidden="true" />
          {t('ads.feed.place')}
        </Link>
      )}
    </section>
  );
}
